import { useState } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/reveal";
import SEO from "@/components/seo";
import {
  Dog,
  Cat,
  PawPrint,
  Footprints,
  Plane,
  Moon,
  Clock,
  Car,
  HeartPulse,
  CalendarCheck,
  Sparkles,
  MapPin,
  Phone,
  RotateCcw,
  ArrowLeft,
  CheckCircle2,
  type LucideIcon,
} from "lucide-react";

const BOOK_A_CALL = "https://calendly.com/carla-tallylassie";

type ServiceKey =
  | "vip"
  | "petPal"
  | "express"
  | "longer"
  | "bnb"
  | "pupventures"
  | "dogTaxi"
  | "compassionate";

interface Recommendation {
  name: string;
  tagline: string;
  priceRange: string;
  description: string;
  href: string;
  icon: LucideIcon;
}

const RECOMMENDATIONS: Record<ServiceKey, Recommendation> = {
  vip: {
    name: "VIP Dog Walking Program",
    tagline: "Recurring weekday walks you can set and forget",
    priceRange: "Custom recurring plan · 30 or 45 min visits",
    description:
      "Our flagship program for busy professionals. Choose your set days, get a dedicated walker, priority holiday booking, automatic billing, and our 100% reliability guarantee.",
    href: "/vip-program",
    icon: Footprints,
  },
  petPal: {
    name: "Pet Pal Visit",
    tagline: "Our most popular all-purpose visit",
    priceRange: "Starts at $35 · 30 minutes",
    description:
      "Perfect for walks plus feeding, let-outs, and home checks while you're at work or away. Every visit comes with photos and a full report, and medication is always free.",
    href: "/menu",
    icon: PawPrint,
  },
  express: {
    name: "Express Care Visit",
    tagline: "A quick, efficient midday check-in",
    priceRange: "Starts at $30 · 15 minutes",
    description:
      "Ideal for a fast let-out, feeding, or litter-box scoop. About half of our cat-sitting clients choose this one. Includes photos and a visit report.",
    href: "/menu",
    icon: Clock,
  },
  longer: {
    name: "Longer Love Visit",
    tagline: "Extra time for multiple pets or extra TLC",
    priceRange: "Starts at $45 · 45 minutes",
    description:
      "Great for multiple pets, longer walks and feeds, several litter boxes, or pets who simply need more attention. Photos and a full report every time.",
    href: "/menu",
    icon: PawPrint,
  },
  bnb: {
    name: "Bed & Breakfast",
    tagline: "Overnight company while you travel",
    priceRange: "Starts at $135 · evening stay + morning visit",
    description:
      "A 2-hour evening stay from dinner to tuck-in, plus a 1-hour morning visit. We keep your pet's routine consistent and tend to mail, lights, plants, and trash while you're away.",
    href: "/menu",
    icon: Moon,
  },
  pupventures: {
    name: "Pupventures",
    tagline: "An off-leash-energy adventure outing",
    priceRange: "Starts at $95 · ~90 min door to door",
    description:
      "For the dog who needs to get out of the house! We safely transport your pup to a local park or greenway for one-on-one time (or a playdate with friends).",
    href: "/menu",
    icon: Sparkles,
  },
  dogTaxi: {
    name: "Dog Taxi",
    tagline: "Safe rides to the vet, groomer, or daycare",
    priceRange: "Starts at $25 in town",
    description:
      "Reliable transport to or from the vet, boarder, groomer, training, daycare, or the grandparents' house — within our service range.",
    href: "/menu",
    icon: Car,
  },
  compassionate: {
    name: "Compassionate Care Visit",
    tagline: "Gentle care for senior or recovering pets",
    priceRange: "Regular visit pricing · meds included free",
    description:
      "Offered by owner Carla McClellan for pets recovering from surgery or in end-of-life care. No extra charge for medication, and we consult with your vet as needed. Availability is limited.",
    href: "/contact-us",
    icon: HeartPulse,
  },
};

interface Option {
  label: string;
  sublabel?: string;
  icon: LucideIcon;
  weights: Partial<Record<ServiceKey, number>>;
  area?: "in" | "extended" | "unsure";
}

interface Question {
  id: string;
  prompt: string;
  options: Option[];
}

const QUESTIONS: Question[] = [
  {
    id: "who",
    prompt: "First — who are we caring for?",
    options: [
      { label: "My dog(s)", icon: Dog, weights: {} },
      { label: "My cat(s)", icon: Cat, weights: { express: 2, petPal: 1 } },
      { label: "Both dogs & cats", icon: PawPrint, weights: { longer: 2, petPal: 1 } },
      { label: "Other small pets", icon: PawPrint, weights: { express: 1, petPal: 1 } },
    ],
  },
  {
    id: "need",
    prompt: "What do you need most?",
    options: [
      { label: "Regular walks while I work", icon: Footprints, weights: { vip: 3, petPal: 1 } },
      { label: "Care while I'm traveling", icon: Plane, weights: { petPal: 2, longer: 1, bnb: 1 } },
      { label: "Overnight company for my pet", icon: Moon, weights: { bnb: 3 } },
      { label: "A quick midday check-in", icon: Clock, weights: { express: 3 } },
      { label: "Rides to vet / groomer / daycare", icon: Car, weights: { dogTaxi: 3 } },
      { label: "Extra TLC for a senior or recovering pet", icon: HeartPulse, weights: { compassionate: 3, longer: 1 } },
    ],
  },
  {
    id: "often",
    prompt: "How often will you need us?",
    options: [
      { label: "A few set days every week", sublabel: "Recurring schedule", icon: CalendarCheck, weights: { vip: 3 } },
      { label: "Just while I'm away", sublabel: "Travel coverage", icon: Plane, weights: { petPal: 1, bnb: 1, longer: 1 } },
      { label: "One-time or once in a while", icon: Clock, weights: { express: 1, petPal: 1, dogTaxi: 1 } },
      { label: "My dog has energy to burn!", sublabel: "Needs an outlet", icon: Sparkles, weights: { pupventures: 3 } },
    ],
  },
  {
    id: "length",
    prompt: "How much time should we spend each visit?",
    options: [
      { label: "Quick & efficient", sublabel: "~15 minutes", icon: Clock, weights: { express: 2 } },
      { label: "The standard visit", sublabel: "~30 minutes", icon: PawPrint, weights: { petPal: 2 } },
      { label: "A nice long visit", sublabel: "~45 minutes", icon: PawPrint, weights: { longer: 2 } },
      { label: "A real outing / adventure", sublabel: "~90 minutes", icon: Sparkles, weights: { pupventures: 2 } },
    ],
  },
  {
    id: "where",
    prompt: "Last one — where's home?",
    options: [
      { label: "In-area Tallahassee neighborhood", sublabel: "City limits", icon: MapPin, weights: {}, area: "in" },
      { label: "Extended area / outside the city", sublabel: "Leon & nearby counties", icon: MapPin, weights: {}, area: "extended" },
      { label: "Not sure — help me check", icon: MapPin, weights: {}, area: "unsure" },
    ],
  },
];

function computeResult(answers: Option[]): { primary: ServiceKey; alternate: ServiceKey | null } {
  const scores: Record<ServiceKey, number> = {
    vip: 0,
    petPal: 0,
    express: 0,
    longer: 0,
    bnb: 0,
    pupventures: 0,
    dogTaxi: 0,
    compassionate: 0,
  };
  for (const ans of answers) {
    for (const [key, val] of Object.entries(ans.weights) as [ServiceKey, number][]) {
      scores[key] += val;
    }
  }
  const ranked = (Object.entries(scores) as [ServiceKey, number][])
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);

  const primary: ServiceKey = ranked.length > 0 ? ranked[0][0] : "petPal";
  const alternate: ServiceKey | null =
    ranked.length > 1 && ranked[1][0] !== primary ? ranked[1][0] : null;
  return { primary, alternate };
}

const AREA_NOTE: Record<string, string> = {
  in: "You're right in our core service area, so availability is usually excellent.",
  extended:
    "You may be in our extended service area — we serve all of Leon County and nearby when we have a sitter to cover you. We'll confirm on our call.",
  unsure:
    "Not sure if you're in our area? No problem — we serve all of Leon County and beyond. We'll check your neighborhood on our call.",
};

export default function CarePlanFinder() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Option[]>([]);

  const totalSteps = QUESTIONS.length;
  const isResult = step >= totalSteps;

  function select(option: Option) {
    const next = [...answers];
    next[step] = option;
    setAnswers(next);
    setStep(step + 1);
  }

  function back() {
    if (step > 0) setStep(step - 1);
  }

  function restart() {
    setStep(0);
    setAnswers([]);
  }

  const progress = Math.round((Math.min(step, totalSteps) / totalSteps) * 100);

  return (
    <div className="py-16 md:py-24 min-h-[80vh]">
      <SEO
        title="Care Plan Finder | Match With the Right Pet Care"
        description="Answer a few quick questions and get a personalized pet care recommendation from Tallahassee Lassie — dog walking, pet sitting, overnights, and more. Then book a free call."
        path="/care-plan-finder"
      />

      <div className="container mx-auto px-4 max-w-3xl">
        <Reveal>
          <div className="text-center mb-10">
            <p className="uppercase tracking-[0.2em] text-sm text-primary/70 mb-3 font-medium">
              Care Plan Finder
            </p>
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-foreground mb-4">
              Find Your Pet's Perfect Care Plan
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Answer five quick questions and we'll match you with the right service and a price range — then you can book a free, no-pressure call with Carla.
            </p>
          </div>
        </Reveal>

        {!isResult && (
          <Reveal delay={100}>
            <div
              className="bg-card border border-border/60 shadow-sm p-6 md:p-10"
              style={{ borderRadius: "32px 0 32px 0" }}
            >
              <div className="mb-8">
                <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
                  <span className="font-medium">
                    Question {step + 1} of {totalSteps}
                  </span>
                  <span>{progress}%</span>
                </div>
                <div className="h-2 w-full bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>

              <h2 className="text-2xl md:text-3xl font-serif font-bold text-foreground mb-6">
                {QUESTIONS[step].prompt}
              </h2>

              <div className="grid sm:grid-cols-2 gap-3">
                {QUESTIONS[step].options.map((option, i) => {
                  const Icon = option.icon;
                  const selected = answers[step]?.label === option.label;
                  return (
                    <button
                      key={i}
                      onClick={() => select(option)}
                      className={`flex items-center gap-4 text-left p-4 border transition-all card-lift ${
                        selected
                          ? "border-primary bg-primary/5"
                          : "border-border/60 bg-white hover:border-primary/40 hover:bg-primary/5"
                      }`}
                      style={{ borderRadius: "16px 0 16px 0" }}
                    >
                      <span className="w-11 h-11 shrink-0 bg-primary/10 text-primary flex items-center justify-center" style={{ borderRadius: "12px 0 12px 0" }}>
                        <Icon size={22} />
                      </span>
                      <span>
                        <span className="block font-serif font-bold text-foreground leading-tight">
                          {option.label}
                        </span>
                        {option.sublabel && (
                          <span className="block text-sm text-muted-foreground">{option.sublabel}</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>

              {step > 0 && (
                <div className="mt-8">
                  <button
                    onClick={back}
                    className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                  >
                    <ArrowLeft size={16} /> Back
                  </button>
                </div>
              )}
            </div>
          </Reveal>
        )}

        {isResult && <ResultCard answers={answers} onRestart={restart} />}
      </div>
    </div>
  );
}

function ResultCard({ answers, onRestart }: { answers: Option[]; onRestart: () => void }) {
  const { primary, alternate } = computeResult(answers);
  const rec = RECOMMENDATIONS[primary];
  const alt = alternate ? RECOMMENDATIONS[alternate] : null;
  const areaAnswer = answers.find((a) => a.area);
  const areaNote = areaAnswer?.area ? AREA_NOTE[areaAnswer.area] : null;
  const Icon = rec.icon;

  return (
    <Reveal>
      <div className="text-center mb-6">
        <p className="uppercase tracking-[0.2em] text-sm text-primary/70 mb-2 font-medium">
          Your match
        </p>
        <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground">
          We'd recommend the {rec.name}
        </h2>
      </div>

      <div
        className="bg-primary/5 border border-primary/20 p-8 md:p-10"
        style={{ borderRadius: "32px 0 32px 0" }}
      >
        <div className="flex items-start gap-5">
          <span className="w-14 h-14 shrink-0 bg-primary text-white flex items-center justify-center shadow-md" style={{ borderRadius: "16px 0 16px 0" }}>
            <Icon size={28} />
          </span>
          <div>
            <h3 className="text-2xl font-serif font-bold text-foreground">{rec.name}</h3>
            <p className="text-primary font-medium">{rec.tagline}</p>
          </div>
        </div>

        <p className="text-lg text-foreground/80 leading-relaxed mt-6">{rec.description}</p>

        <div className="mt-6 inline-flex items-center gap-2 bg-white border border-primary/20 px-4 py-2 font-bold text-primary" style={{ borderRadius: "12px 0 12px 0" }}>
          {rec.priceRange}
        </div>

        {areaNote && (
          <p className="mt-6 flex items-start gap-2 text-muted-foreground">
            <MapPin size={18} className="text-secondary mt-1 shrink-0" /> {areaNote}
          </p>
        )}

        <div className="mt-8 flex flex-col sm:flex-row gap-4">
          <Button asChild size="lg" className="h-14 px-8 text-lg w-full sm:w-auto">
            <a href={BOOK_A_CALL} target="_blank" rel="noopener noreferrer">
              <Phone size={18} className="mr-2" /> Book a Free Call
            </a>
          </Button>
          <Button
            asChild
              size="lg"
              variant="outline"
              className="h-14 px-8 text-lg border-primary text-primary hover:bg-primary hover:text-white w-full sm:w-auto"
            >
            <Link href={rec.href}>
              See the details
            </Link>
          </Button>
        </div>
      </div>

      {alt && (
        <div className="mt-6 bg-card border border-border/60 p-6" style={{ borderRadius: "24px 0 24px 0" }}>
          <p className="text-sm font-medium text-muted-foreground mb-2">You might also consider</p>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={20} className="text-secondary shrink-0" />
              <div>
                <span className="font-serif font-bold text-foreground">{alt.name}</span>
                <span className="text-muted-foreground"> — {alt.priceRange}</span>
              </div>
            </div>
            <Button asChild variant="ghost" className="text-primary hover:text-primary/80">
              <Link href={alt.href}>
                Learn more &rarr;
              </Link>
            </Button>
          </div>
        </div>
      )}

      <div className="mt-8 text-center">
        <button
          onClick={onRestart}
          className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
        >
          <RotateCcw size={16} /> Start over
        </button>
        <p className="mt-4 text-sm text-muted-foreground">
          Want to compare everything?{" "}
          <Link href="/menu" className="text-primary underline-offset-4 hover:underline">
            View all our services
          </Link>
        </p>
      </div>
    </Reveal>
  );
}
